import { Badge, CardActionArea, CardHeader } from '@mui/material';

import type { Dispatch, SetStateAction } from 'react';

import type { Or2CardProps } from '@/components/Surface/Or2Card';
import { Or2Card } from '@/components/Surface/Or2Card';
import { graphql, type DocumentType } from '@/gql';
import { UserAvatar } from '@/modules/user/components/UserAvatar';
import { getAuthorName } from '@/modules/group/noticeboard/noticeboardUtils';
import { GroupOrServiceAvatar } from '@/components/Avatar/GroupOrServiceAvatar';

const PINNED_POST_SUMMARY_FRAGMENT = graphql(`
  fragment pinnedPostSummaryFragment on Post {
    _id
    title
    author {
      _id
      firstName
      lastName
      ...userImageFields
    }
    authorContext
    owner {
      _id
      name
      iconUrl
    }
  }
`);

interface Props extends Or2CardProps {
  post: DocumentType<typeof PINNED_POST_SUMMARY_FRAGMENT>;
  selected: boolean;
  setSelectedPostId: Dispatch<SetStateAction<string | null>>;
  showOwner?: boolean;
}

export const PinnedPostSummary = ({
  post,
  selected,
  setSelectedPostId,
  showOwner,
  ...other
}: Props) => {
  const { _id: postId, title, author, owner } = post;

  const authorName = getAuthorName(post);

  return (
    <Or2Card
      selected={selected}
      hoverHighlight
      borderedVariant="highlighted"
      onClick={() =>
        setSelectedPostId((selectedPostId) =>
          selectedPostId === postId ? null : postId,
        )
      }
      {...other}
    >
      <CardActionArea disableRipple>
        <CardHeader
          avatar={
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                showOwner ? (
                  <GroupOrServiceAvatar
                    iconUrl={owner?.iconUrl}
                    groupOrServiceName={owner?.name}
                    size={20}
                  />
                ) : null
              }
            >
              <UserAvatar user={author} size={32} />
            </Badge>
          }
          title={<strong>{title}</strong>}
          subheader={`${authorName}${showOwner ? ` (${owner?.name})` : ''}`}
        />
      </CardActionArea>
    </Or2Card>
  );
};
